import React from "react";
import { Alert, Badge, Button, Pagination, Tag } from "antd";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import APIService from "../../Service/APIService";
import CommonButton from "../../common/button/button.js";
import Table from "../../common/table";
import { hasPermission } from "../../Config/permissions.js";
import { useAuth } from "../AuthProvider.js";
import { ACCOUNT_CHANGE_FIELD_LABELS } from "../Account/accountChangeRequestFields.js";
import AccountUpdateRequestReviewModal from "./AccountUpdateRequestReviewModal.js";
import "./AccountUpdateRequests.css";

const DATE_FORMAT = "MM-DD-YYYY hh:mm A";
const STATUS_FILTERS = [
  { value: "Pending", label: "Pending" },
  {
    value: "Approved",
    label: "Approved",
    icon: <CheckCircleFilled style={{ color: "#27a907" }} />,
  },
  {
    value: "Rejected",
    label: "Rejected",
    icon: <ExclamationCircleFilled style={{ color: "#ff0000" }} />,
  },
];

const AccountUpdateRequestsTab = ({ searchKeyword = "" }) => {
  const { user } = useAuth();
  const apiRef = React.useRef(new APIService());
  const [rows, setRows] = React.useState([]);
  const [status, setStatus] = React.useState("Pending");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selectedRequestId, setSelectedRequestId] = React.useState(null);
  const [sortState, setSortState] = React.useState({
    field: "createdAt",
    order: "desc",
  });
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchKeyword);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const canReview = hasPermission(user, "manage_accounts.edit");

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchKeyword);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchKeyword]);

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const query = new URLSearchParams({
          status,
          page: String(page),
          pageSize: String(pageSize),
          search: debouncedSearch || "",
          sortField: sortState?.field || "createdAt",
          sortOrder: sortState?.order || "desc",
        });
        const response = await apiRef.current.getDataFromBackend(
          `getaccountupdaterequests?${query.toString()}`,
        );
        if (!active) return;
        setRows(response?.data?.data || []);
        setTotalCount(Number(response?.data?.totalCount) || 0);
      } catch (requestError) {
        if (!active) return;
        setRows([]);
        setTotalCount(0);
        setError(
          requestError?.response?.data?.message ||
            "Unable to load account update requests.",
        );
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [debouncedSearch, page, pageSize, refreshKey, sortState, status]);

  const columns = React.useMemo(
    () => [
      {
        headerName: "Account",
        field: "AccountID",
        sortable: false,
        minWidth: 220,
        cellRenderer: (params) =>
          params.data?.Account?.AccountName ||
          `Account ${params.data?.AccountID}`,
      },
      {
        headerName: "Requester",
        field: "RequesterName",
        sortable: false,
        minWidth: 180,
      },
      {
        headerName: "Requested Fields",
        field: "RequestedFields",
        sortable: false,
        minWidth: 300,
        cellRenderer: (params) =>
          (params.data?.RequestedFields || [])
            .map((field) => ACCOUNT_CHANGE_FIELD_LABELS[field] || field)
            .join(", "),
      },
      {
        headerName: "Submitted",
        field: "createdAt",
        minWidth: 190,
        cellRenderer: (params) =>
          params.data?.createdAt
            ? dayjs(params.data.createdAt).format(DATE_FORMAT)
            : "—",
      },
      {
        headerName: "Status",
        field: "Status",
        minWidth: 130,
        cellRenderer: (params) => {
          const value = params.data?.Status;
          const color =
            value === "Approved"
              ? "green"
              : value === "Rejected"
                ? "red"
                : "gold";
          return <Tag color={color}>{value}</Tag>;
        },
      },
      {
        headerName: "Reviewer",
        field: "ReviewerName",
        sortable: false,
        minWidth: 170,
      },
      {
        headerName: "Reviewed",
        field: "ReviewedAt",
        minWidth: 190,
        cellRenderer: (params) =>
          params.data?.ReviewedAt
            ? dayjs(params.data.ReviewedAt).format(DATE_FORMAT)
            : "—",
      },
      {
        headerName: "Action",
        field: "Action",
        sortable: false,
        filter: false,
        minWidth: 130,
        cellRenderer: (params) => (
          <CommonButton
            variant="table"
            subVariant="success"
            style={{ minWidth: "80px" }}
            onClick={() => setSelectedRequestId(params.data?.ID)}
          >
            {canReview && params.data?.Status === "Pending" ? "Review" : "View"}
          </CommonButton>
        ),
      },
    ],
    [canReview],
  );

  return (
    <div className="account-update-requests-tab">
      <div className="account-update-requests-toolbar">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.value}
            ghost={status !== filter.value}
            style={{ color: "#444", borderColor: "#444" }}
            onClick={() => {
              setStatus(filter.value);
              setPage(1);
            }}
          >
            {filter.icon ? <Badge count={filter.icon} /> : null}
            {filter.label}
          </Button>
        ))}
      </div>
      {error ? <Alert type="error" showIcon message={error} /> : null}
      <Table
        rowData={rows}
        columnDefs={columns}
        loading={loading}
        pagination={false}
        serverSideSort
        sortState={sortState}
        onSortChange={(next) => {
          setSortState(next || { field: "createdAt", order: "desc" });
          setPage(1);
        }}
      />
      <div className="account-update-pagination">
        <Pagination
          current={page}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          pageSizeOptions={[10, 20, 50, 100]}
          showTotal={(total) => `${total} request${total === 1 ? "" : "s"}`}
          onChange={(nextPage, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          }}
        />
      </div>
      <AccountUpdateRequestReviewModal
        requestId={selectedRequestId}
        open={Boolean(selectedRequestId)}
        canReview={canReview}
        onClose={() => setSelectedRequestId(null)}
        onReviewed={() => setRefreshKey((value) => value + 1)}
      />
    </div>
  );
};

export default AccountUpdateRequestsTab;
